import LogoReverse from 'assets/images/logo-cyan192.png'
import { toast, ToastOptions } from 'react-toastify'
import styled from 'styled-components'
import { DesktopNotificationPermissionEnum } from './types'

// Define default toast options
const defaultToastOptions: ToastOptions = {
  closeOnClick: true,
  hideProgressBar: true,
  theme: 'colored',
  autoClose: 5000,
  position: 'top-center',
}

const withCustomStyles = (styles: React.CSSProperties) => ({
  ...defaultToastOptions,
  style: styles,
})

const createMessage = (
  type: 'error' | 'success' | 'info' | 'warn' | 'loading',
  heading?: string | JSX.Element,
  message?: string | JSX.Element,
) => (
  // add type to style  pass as parameter to the function
  <Div type={type}>
    {heading && <strong>{heading}</strong>}
    {message && <div>{message}</div>}
  </Div>
)

interface NotificatorParams {
  type: 'error' | 'success' | 'info' | 'warn' | 'loading'
  heading?: string | JSX.Element
  message?: string | JSX.Element
  toastOptions?: ToastOptions<Record<string, never>>
}

// Generalized toast function
const notify = ({ type, heading, message, toastOptions }: NotificatorParams): string | number =>
  toast[type](createMessage(type, heading, message), {
    ...withCustomStyles({
      color: `${({ theme, type }: any) =>
        type === 'loading'
          ? theme.colors.grey.darker
          : type === 'warn'
          ? theme.colors.warning.darker
          : theme.colors[type].darker}`,
      backgroundColor: `${({ theme, type }: any) =>
        type === 'loading'
          ? theme.colors.grey.lighter
          : type === 'warn'
          ? theme.colors.warning.lighter
          : theme.colors[type].lighter}`,
      border: `${({ theme, type }: any) =>
        `1px solid ${
          type === 'loading'
            ? theme.colors.grey.light
            : type === 'warn'
            ? theme.colors.warning.light
            : theme.colors[type].light
        }`}`,
      borderRadius: `${({ theme }: any) => theme.borderRadius.rounded}!important`,
    }),
    ...toastOptions,
  })

// Desktop notification functions
const getDesktopNotificationPermission = async (): Promise<keyof typeof DesktopNotificationPermissionEnum> => {
  const permission =
    Notification.permission === DesktopNotificationPermissionEnum.granted
      ? Notification.permission
      : await Notification.requestPermission()
  return permission
}

const isDesktopNotificationGranted = (): boolean =>
  Notification.permission === DesktopNotificationPermissionEnum.granted

const isDesktopNotificationsNotSelected = (): boolean =>
  Notification.permission === DesktopNotificationPermissionEnum.default

const planAgentReadyNotification = (clickHandler: () => void): void => {
  const notification = {
    title: 'Click to take over the call!',
    clickHandler,
    options: {
      body: 'UHC Payer Agent is Ready.',
      icon: LogoReverse,
      badge: LogoReverse,
      vibrate: [200, 100, 200],
      requireInteraction: true,
    },
  }

  const newNotification = new Notification(notification.title, notification.options)
  newNotification.onclick = () => {
    notification.clickHandler()
    parent.focus()
    window.focus() // just in case, older browsers
  }
}

// Exporting all notification functions
export const notificator = {
  notify,
  getDesktopNotificationPermission,
  isDesktopNotificationGranted,
  planAgentReadyNotification,
  isDesktopNotificationsNotSelected,
  defaultToastOptions,
}

const Div = styled.div<{ type: 'error' | 'success' | 'info' | 'warn' | 'loading' }>`
  display: flex;
  flex-direction: column;

  strong {
    color: ${({ type, theme }) =>
      type === 'loading'
        ? theme.colors.grey.darker
        : type === 'warn'
        ? theme.colors.warning.darker
        : theme.colors[type].darker};
    ${({ theme }) => theme.typography.s.medium};
    text-align: left;
  }

  div {
    ${({ theme }) => theme.typography.s.normal};
    color: ${({ theme }) => theme.colors.neutral.neutral};
    margin-top: ${({ theme }) => theme.spacing.xxs};
  }
`
